import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { Lock, Mail, Zap } from "lucide-react";
import { BASE_URL } from "../config";

const Login = () => {
  const [form, setForm] = useState({ email: "", password: "" });
  const [message, setMessage] = useState("");
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${BASE_URL}login.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
        credentials: "include",
      });
      const data = await res.json();
      setMessage(data.message);

      if (data.status === "success") {
        localStorage.setItem("user", JSON.stringify(data.user));
        navigate("/analytics-dashboard");
      }
    } catch (err) {
      setMessage("Error connecting to server");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a0e1a] p-4 relative overflow-hidden">
      {/* Enhanced radial gradients */}
      <div className="absolute top-[-300px] right-[-300px] w-[1200px] h-[1200px] bg-blue-500/30 rounded-full blur-[180px]"></div>
      <div className="absolute bottom-[-400px] left-[-400px] w-[1400px] h-[1400px] bg-cyan-500/20 rounded-full blur-[200px]"></div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-indigo-500/10 rounded-full blur-[150px]"></div>

      <div className="relative z-10 w-full max-w-md">
        {/* Logo/Brand with glow effect */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-2">
            <div className="p-3 rounded-xl bg-gradient-to-br from-cyan-500/30 to-blue-500/30 
              shadow-lg" style={{ boxShadow: '0 0 30px rgba(34, 211, 238, 0.5)' }}>
              <Zap size={32} className="text-cyan-400" />
            </div>
          </div>
          <h1 className="text-5xl font-bold text-white mb-2" 
            style={{ textShadow: '0 0 20px rgba(96, 165, 250, 0.5)' }}>
            Convex
          </h1>
          <p className="text-gray-400 text-sm">Call Analytics Dashboard</p>
        </div>

        {/* Login Card with bottom glow */}
        <div className="relative group bg-[#0f1419]/90 backdrop-blur-xl shadow-2xl rounded-2xl p-8 
          border border-gray-800/50">
          {/* Bottom glow line */}
          <div
            className="absolute bottom-0 left-0 right-0 h-[4px] bg-gradient-to-r from-cyan-400 via-blue-400 to-blue-500 
            opacity-80 transition-all duration-300"
            style={{
              boxShadow: '0 0 30px rgba(34, 211, 238, 0.7), 0 0 60px rgba(34, 211, 238, 0.4)',
              filter: 'brightness(1.2)',
            }}
          />

          <h2 className="text-2xl font-bold text-white mb-6">Welcome Back</h2>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                <Mail size={20} />
              </div>
              <input
                type="email"
                name="email"
                placeholder="Email"
                value={form.email}
                onChange={handleChange}
                required
                className="w-full pl-12 pr-4 py-3 rounded-xl bg-[#161b22] text-white placeholder-gray-500 
                border border-gray-800/50 focus:outline-none focus:ring-2 focus:ring-cyan-500 
                focus:border-transparent transition-all duration-200"
                style={{ boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.3)' }}
              />
            </div>

            <div className="relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                <Lock size={20} />
              </div>
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder="Password"
                value={form.password}
                onChange={handleChange}
                required
                className="w-full pl-12 pr-12 py-3 rounded-xl bg-[#161b22] text-white placeholder-gray-500 
                border border-gray-800/50 focus:outline-none focus:ring-2 focus:ring-cyan-500 
                focus:border-transparent transition-all duration-200"
                style={{ boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.3)' }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition"
              >
                {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
              </button>
            </div>

            <p
              className="text-right text-sm text-cyan-400 cursor-pointer hover:text-cyan-300 transition"
              onClick={() => navigate("/forgot-password")}
            >
              Forgot Password?
            </p>

            <button
              type="submit"
              className="w-full py-3 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 
              text-white font-semibold rounded-xl shadow-lg transition-all duration-300 
              hover:-translate-y-0.5"
              style={{ boxShadow: '0 4px 20px rgba(34, 211, 238, 0.4)' }}
            >
              Login
            </button>
          </form>

          {message && (
            <p className={`mt-4 text-center text-sm ${
              message.includes('Error') || message.includes('Invalid') ? 'text-rose-400' : 'text-emerald-400'
            }`}>
              {message}
            </p>
          )}

          {/* Signup link */}
          <div className="mt-6 text-center text-sm text-gray-400">
            Don't have an account?{" "}
            <span 
              className="text-cyan-400 cursor-pointer hover:text-cyan-300 transition"
              onClick={() => navigate("/signup")}
            >
              Sign up
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
